"use client";

import {
  Cpu,
  Download,
  HardDrive,
  Laptop2,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import {
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { PUBLIC_PRODUCT_LINKS } from "@/config/publicProductLinks";
import { useLanguage } from "@/contexts/LanguageContext";
import { getMacDownloadContent } from "@/i18n/macDownloadContent";

type MacDownloadModalProps = {
  label: string;
  triggerClassName: string;
  children: ReactNode;
};

export default function MacDownloadModal({
  label,
  triggerClassName,
  children,
}: MacDownloadModalProps) {
  const { locale } = useLanguage();
  const copy = getMacDownloadContent(locale);
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const downloadRef = useRef<HTMLAnchorElement>(null);
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusTimer = window.setTimeout(() => downloadRef.current?.focus(), 0);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        window.setTimeout(() => triggerRef.current?.focus(), 0);
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  function close() {
    setOpen(false);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        className={triggerClassName}
        aria-label={label}
      >
        {children}
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-[120] grid place-items-center bg-slate-950/70 p-3 backdrop-blur-md sm:p-4"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) close();
          }}
        >
          <section
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="relative max-h-[92vh] w-full max-w-[27.5rem] overflow-y-auto rounded-[1.65rem] border border-white/15 bg-[radial-gradient(circle_at_top_right,_rgba(124,58,237,0.45),_transparent_38%),radial-gradient(circle_at_bottom_left,_rgba(34,211,238,0.15),_transparent_34%),linear-gradient(145deg,#070b1d_0%,#17113f_52%,#262261_100%)] p-5 text-white shadow-[0_32px_100px_rgba(15,23,42,0.58)] sm:p-6"
          >
            <div className="pointer-events-none absolute -right-14 -top-14 h-44 w-44 rounded-full border border-white/10" />
            <div className="pointer-events-none absolute -bottom-16 -left-14 h-48 w-48 rounded-full border border-cyan-200/10" />

            <button
              type="button"
              onClick={close}
              aria-label={copy.close}
              className="absolute right-3.5 top-3.5 z-10 grid h-8 w-8 place-items-center rounded-full border border-white/10 bg-white/10 text-slate-200 transition hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>

            <div className="relative">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-200/20 bg-violet-300/10 px-2.5 py-1.5 text-[0.64rem] font-black uppercase tracking-[0.13em] text-violet-100">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                {copy.eyebrow}
              </span>

              <span className="mt-4 grid h-11 w-11 place-items-center rounded-xl border border-white/10 bg-white/10 text-violet-100 shadow-lg shadow-violet-950/30">
                <Laptop2 className="h-[22px] w-[22px]" aria-hidden="true" />
              </span>

              <h2 id={titleId} className="mt-4 pr-9 text-[1.55rem] font-black leading-tight tracking-[-0.025em] sm:text-[1.7rem]">
                {copy.title}
              </h2>
              <p className="mt-2.5 text-[0.82rem] leading-[1.35rem] text-slate-200">{copy.body}</p>

              <div className="mt-[18px] grid grid-cols-2 gap-2">
                <InfoTile icon={<ShieldCheck className="h-4 w-4" />} title={copy.securityTitle} detail={copy.securityDetail} />
                <InfoTile icon={<Cpu className="h-4 w-4" />} title={copy.compatibilityTitle} detail={copy.compatibilityDetail} />
                <InfoTile icon={<HardDrive className="h-4 w-4" />} title={copy.sizeTitle} detail={copy.sizeDetail} />
                <InfoTile icon={<Laptop2 className="h-4 w-4" />} title={copy.requirementTitle} detail={copy.requirementDetail} />
              </div>

              <a
                ref={downloadRef}
                href={PUBLIC_PRODUCT_LINKS.interviewCopilotMac}
                onClick={close}
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-black text-slate-950 shadow-xl shadow-violet-950/30 transition hover:-translate-y-0.5 hover:bg-violet-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200 motion-reduce:transform-none"
              >
                <Download className="h-[18px] w-[18px] text-violet-700" aria-hidden="true" />
                {copy.download}
              </a>

              <button
                type="button"
                onClick={close}
                className="mt-2 w-full rounded-xl border border-white/10 bg-white/[0.06] px-5 py-2.5 text-sm font-extrabold text-white transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                {copy.cancel}
              </button>

              <p className="mt-3 text-center text-[0.72rem] font-medium leading-5 text-white/90">{copy.footer}</p>
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}

function InfoTile({
  icon,
  title,
  detail,
}: {
  icon: ReactNode;
  title: string;
  detail: string;
}) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.075] p-3 backdrop-blur-sm">
      <span className="grid h-7 w-7 place-items-center rounded-lg bg-white/10 text-cyan-100 ring-1 ring-white/10" aria-hidden="true">
        {icon}
      </span>
      <p className="mt-2.5 text-[0.7rem] font-black text-white">{title}</p>
      <p className="mt-1 text-[0.68rem] font-semibold leading-4 text-white/90">{detail}</p>
    </div>
  );
}
